import { useState } from "react";

import { DataTableFacetedFilter } from "@/components/data-table/DataTableFacetedFilter";
import { useDebounce } from "@/utils/helpers";

import type { EntityDescriptor } from "./EntityDescriptor";
import { useEntitySelection } from "./useEntitySelection";

interface Props<T> {
  title: string;
  descriptor: EntityDescriptor<T>;
  value: number[];
  onChange: (ids: number[]) => void;
}

type Opt = { id: string | number; name: string };

/**
 * The one filter box: multi-select chip + checkbox dropdown, driven by a
 * descriptor. Bounded entities browse their cached list; unbounded ones search
 * the server (selected pinned on top). For form pickers use EntityCombobox.
 */
export function EntityFilter<T>(props: Props<T>) {
  return props.descriptor.bounded ? (
    <BrowseFilter {...props} />
  ) : (
    <SearchFilter {...props} />
  );
}

// Bounded: the full cached list is the options; the renderer filters + pins in-memory.
function BrowseFilter<T>({ title, descriptor, value, onChange }: Props<T>) {
  const { data, isLoading } = descriptor.useList!();
  const options: Opt[] = (data ?? []).map((e) => ({
    id: descriptor.getEntityId(e),
    name: descriptor.renderLabel(e),
  }));
  const valueOpts = options.filter((o) => value.includes(Number(o.id)));
  return (
    <DataTableFacetedFilter
      mode="multi"
      title={title}
      options={options}
      value={valueOpts}
      onChange={(opts) => onChange(opts.map((o) => Number(o.id)))}
      isLoading={isLoading}
    />
  );
}

// Unbounded: server-search; pass results as options + resolved selected pinned on top.
function SearchFilter<T>({ title, descriptor, value, onChange }: Props<T>) {
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 200);
  const sel = useEntitySelection<T>({
    value,
    query: debounced,
    useSearchResults: descriptor.useEntities,
    useResolveSelected: descriptor.useResolveSelected,
    getId: descriptor.getEntityId,
  });
  const toOpt = (e: T): Opt => ({
    id: descriptor.getEntityId(e),
    name: descriptor.renderLabel(e),
  });
  const selectedOptions = sel.selectedEntities.map(toOpt);
  return (
    <DataTableFacetedFilter
      mode="multi"
      title={title}
      options={sel.results.map(toOpt)}
      value={selectedOptions}
      onChange={(opts) => onChange(opts.map((o) => Number(o.id)))}
      searchable={{
        query,
        onQueryChange: setQuery,
        isLoading: sel.isLoading,
        selectedOptions,
      }}
    />
  );
}
